import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import { API, getAuth, clearAuth } from '../api'; // adjust path to where api.js lives
import './Profile.css';
import {
 AVATAR_OPTIONS,
 calculateAgeFromDateOfBirth,
 formatDateOfBirth,
 getTrainingBackgroundLabel,
 loadProfileExtras,
 saveProfileExtras,
 splitDateOfBirth,
 TRAINING_BACKGROUND_OPTIONS,
} from '../utils/profileExtras';
import { HelpCircleIcon } from './Icons';

// ─── ICONS ───

function EditIcon({ size = 16, color = '#5CB89A' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
 <path
 d="M11 4H4C3.47 4 2.96 4.21 2.59 4.59C2.21 4.96 2 5.47 2 6V20C2 20.53 2.21 21.04 2.59 21.41C2.96 21.79 3.47 22 4 22H18C18.53 22 19.04 21.79 19.41 21.41C19.79 21.04 20 20.53 20 20V13"
 stroke={color}
 strokeWidth={2}
 strokeLinecap="round"
 strokeLinejoin="round"
 />
 <path
 d="M18.5 2.5C18.89 2.11 19.42 1.89 19.98 1.89C20.53 1.89 21.06 2.11 21.45 2.5C21.84 2.89 22.06 3.42 22.06 3.98C22.06 4.53 21.84 5.06 21.45 5.45L12 14.93L8 16L9.07 12L18.5 2.5Z"
 stroke={color}
 strokeWidth={2}
 strokeLinecap="round"
 strokeLinejoin="round"
 />
 </svg>
 );
}

function SettingsIcon({ size = 22, color = '#434F4D' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
 <circle cx="12" cy="12" r="3" stroke={color} strokeWidth={1.8} />
 <path
 d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
 stroke={color}
 strokeWidth={1.8}
 strokeLinecap="round"
 strokeLinejoin="round"
 />
 </svg>
 );
}

function HelpIcon({ size = 22, color = '#434F4D' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
 <circle cx="12" cy="12" r="10" stroke={color} strokeWidth={1.8} />
 <path
 d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13"
 stroke={color}
 strokeWidth={1.8}
 strokeLinecap="round"
 strokeLinejoin="round"
 />
 <circle cx="12" cy="17" r="0.5" fill={color} stroke={color} strokeWidth={0.5} />
 </svg>
 );
}

function LogOutIcon({ size = 22, color = '#DC4545' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
 <path
 d="M9 21H5C4.47 21 3.96 20.79 3.59 20.41C3.21 20.04 3 19.53 3 19V5C3 4.47 3.21 3.96 3.59 3.59C3.96 3.21 4.47 3 5 3H9"
 stroke={color}
 strokeWidth={1.8}
 strokeLinecap="round"
 strokeLinejoin="round"
 />
 <path d="M16 17L21 12L16 7" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
 <path d="M21 12H9" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
 </svg>
 );
}

function UserInfoIcon({ size = 16, color = '#5CB89A' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
 <circle cx="9" cy="7" r="4" />
 <path d="M22 11l-3 3-2-2" />
 </svg>
 );
}

function NoteIcon({ size = 16, color = '#5CB89A' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
 <polyline points="14 2 14 8 20 8" />
 <line x1="9" y1="13" x2="15" y2="13" />
 <line x1="9" y1="17" x2="15" y2="17" />
 </svg>
 );
}

function ChevronRightIcon({ size = 18, color = '#A0ABA8' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <polyline points="9 18 15 12 9 6" />
 </svg>
 );
}

function SpinnerIcon({ size = 36, color = '#5CB89A' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="profile-spinner">
 <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" strokeOpacity="0.25" />
 <path d="M22 12a10 10 0 0 0-10-10" stroke={color} strokeWidth="3" strokeLinecap="round" />
 </svg>
 );
}

function CloseIcon({ size = 18, color = '#434F4D' }) {
 return (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <path d="M6 6l12 12M18 6L6 18" />
 </svg>
 );
}

// Sleep goal helpers
const parseSleepGoal = (value) => {
 if (value == null || value === '') return null;
 if (typeof value === 'number' && Number.isFinite(value)) return value;

 const numericValue = Number(value);
 if (Number.isFinite(numericValue)) return numericValue;

 if (typeof value === 'string') {
 const hourMatch = value.match(/(\d+)\s*h/i);
 const minuteMatch = value.match(/(\d+)\s*m/i);
 const hours = hourMatch ? Number(hourMatch[1]) : 0;
 const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
 if (hours || minutes) return hours * 60 + minutes;
 }

 return null;
};

// Sleep goal: stored as integer minutes for the backend; labels stay UI-only.
const SLEEP_GOAL_OPTIONS = (() => {
 const out = [];
 for (let h = 5; h <= 11; h++) {
 out.push({ label: `${h}h`, value: h * 60 });
 if (h < 11) out.push({ label: `${h}h 30m`, value: h * 60 + 30 });
 }
 return out;
})();

// Tooltip component for field explanations
function FieldTooltip({ content }) {
 const [isVisible, setIsVisible] = useState(false);
 const [position, setPosition] = useState({ top: 0, left: 0, arrowLeft: 0 });
 const triggerRef = useRef(null);

 const handleMouseEnter = () => {
 if (!triggerRef.current) return;
 
 const rect = triggerRef.current.getBoundingClientRect();
 const tooltipWidth = 360;
 const tooltipHeight = 100; // approximate height
 const viewportWidth = window.innerWidth;
 const viewportHeight = window.innerHeight;

 // Calculate horizontal position (centered on icon, but keep within viewport)
 let left = rect.left + rect.width / 2 - tooltipWidth / 2;
 
 // Ensure tooltip stays within viewport horizontally
 if (left < 10) {
 left = 10;
 } else if (left + tooltipWidth > viewportWidth - 10) {
 left = viewportWidth - tooltipWidth - 10;
 }

 // Calculate arrow position (relative to tooltip)
 const arrowLeft = rect.left + rect.width / 2 - left;

 // Calculate vertical position (prefer above, fallback to below)
 let top;
 let position;
 const gapAbove = 14;
 const gapBelow = 24;
 
 if (rect.top - tooltipHeight - gapAbove > 10) {
 // Position above with smaller gap for Typical Day
 top = rect.top - tooltipHeight - gapAbove;
 position = 'above';
 } else {
 // Position below with larger gap for Training Background
 top = rect.bottom + gapBelow;
 position = 'below';
 }

 setPosition({ top, left, arrowLeft, position });
 setIsVisible(true);
 };

 const handleMouseLeave = () => setIsVisible(false);

 return (
 <>
 <div className="profile-field-tooltip-wrapper" ref={triggerRef}>
 <button
 type="button"
 className="profile-tooltip-trigger"
 onMouseEnter={handleMouseEnter}
 onMouseLeave={handleMouseLeave}
 onClick={() => {
 if (isVisible) {
 setIsVisible(false);
 } else {
 handleMouseEnter();
 }
 }}
 aria-label="Show help"
 >
 <HelpCircleIcon size={16} color="#A0ABA8" />
 </button>
 </div>
 {isVisible &&
 ReactDOM.createPortal(
 <div
 className="profile-tooltip-portal"
 style={{
 position: 'fixed',
 top: `${position.top}px`,
 left: `${position.left}px`,
 zIndex: 9999,
 }}
 onMouseEnter={handleMouseEnter}
 onMouseLeave={handleMouseLeave}
 >
 <div className="profile-tooltip-content" dangerouslySetInnerHTML={{ __html: content }} />
 <div
 className="profile-tooltip-arrow"
 data-position={position.position}
 style={{
 position: 'absolute',
 left: `${position.arrowLeft}px`,
 }}
 />
 </div>,
 document.body
 )
 }
 </>
 );
}

// ─── BACKEND HELPERS ───
// getAuth() and clearAuth() come from ../api — see api.js.

async function fetchUserProfile(token, userId) {
 try {
 const response = await fetch(API.userById(userId), {
 method: 'GET',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 credentials: 'include',
 });
 if (!response.ok) return null;
 return await response.json();
 } catch (e) {
 console.error('Error fetching profile:', e);
 return null;
 }
}

async function updateUserProfile(token, userId, data) {
 try {
 const response = await fetch(API.updateUser(userId), {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 credentials: 'include',
 body: JSON.stringify({
 firstName: data.firstName,
 lastName: data.lastName,
 age: data.age,
 birthDate: data.birthDate,
 gender: data.gender,
 clinicalStory: data.clinicalStory,
 notes: data.notes,
 height: data.height,
 weight: data.weight,
 sleepGoal: data.sleepGoal,
 // Replaces the old activityLevel field — backend now splits it.
 typicalDay: data.typicalDay,
 trainingBackground: data.trainingBackground,
 profilePhoto: data.profilePhoto,
 avatarId: data.avatarId,
 }),
 });
 if (!response.ok) return null;
 return await response.json();
 } catch (e) {
 console.error('Error updating profile:', e);
 return null;
 }
}

function readFileAsDataUrl(file) {
 return new Promise((resolve, reject) => {
 const reader = new FileReader();
 reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
 reader.onerror = reject;
 reader.readAsDataURL(file);
 });
}

async function logoutBackend(token) {
 try {
 await fetch(API.logout(), {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 credentials: 'include',
 });
 } catch {
 // ignore
 }
}

// ─── EDIT PROFILE MODAL ───

function EditProfileModal({ open, onClose, onSave, formData, setFormData, isSaving, birthDateError, setBirthDateError }) {
 const fileInputRef = useRef(null);
 const [fileInputKey, setFileInputKey] = useState(0);
 const [modalErrorMsg, setModalErrorMsg] = useState(null);

 useEffect(() => {
 if (!open) return;
 const onKey = (e) => { if (e.key === 'Escape') onClose(); };
 window.addEventListener('keydown', onKey);
 return () => window.removeEventListener('keydown', onKey);
 }, [open, onClose]);

 if (!open) return null;

 // Replaces the old ACTIVITY_LEVEL_OPTIONS — matches the mobile onboarding.
 const TYPICAL_DAY_OPTIONS = ['', 'Mostly Sitting', 'Mostly Standing', 'Mostly Moving'];
 const GENDER_OPTIONS = ['', 'Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say'];

 const initialSource = (formData.firstName || formData.lastName || 'U').trim();
 const initial = initialSource.charAt(0).toUpperCase();
 const hasPhoto = !!formData.profilePhoto;
 const selectedAvatar = AVATAR_OPTIONS.find((option) => option.id === formData.avatarId);

 return ReactDOM.createPortal(
 <div className="profile-modal-overlay" onClick={onClose}>
 <div className="profile-modal-card" onClick={(e) => e.stopPropagation()}>
 {/* Header */}
 <div className="profile-modal-header">
 <button className="profile-modal-cancel" onClick={onClose} type="button">
 Cancel
 </button>
 <h3 className="profile-modal-title">Edit Profile</h3>
 <button
 className="profile-modal-save"
 onClick={onSave}
 disabled={isSaving}
 type="button"
 >
 {isSaving ? 'Saving...' : 'Save'}
 </button>
 </div>

 {/* Error Message */}
 {modalErrorMsg && (
 <div className="profile-modal-error">
 {modalErrorMsg}
 <button onClick={() => setModalErrorMsg(null)} className="profile-modal-error-close" type="button">×</button>
 </div>
 )}

 {/* Avatar Preview */}
 <div className="profile-modal-avatar-section">
 <div className="profile-modal-avatar">
 {hasPhoto ? (
 <img src={formData.profilePhoto} alt="Profile" className="profile-modal-avatar-image" />
 ) : selectedAvatar?.emoji ? (
 <span className="profile-modal-avatar-emoji">{selectedAvatar.emoji}</span>
 ) : (
 <span className="profile-modal-avatar-letter">{initial}</span>
 )}
 </div>
 <label className="profile-photo-upload-btn">
 <input
 key={fileInputKey}
 type="file"
 accept="image/*"
 className="profile-photo-input"
 ref={fileInputRef}
 onChange={async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;

 // Validate file size (max 5MB)
 const MAX_SIZE = 5 * 1024 * 1024; // 5MB
 if (file.size > MAX_SIZE) {
 setModalErrorMsg('Image is too large. Please upload an image under 5MB.');
 setFileInputKey(prev => prev + 1);
 return;
 }

 // Validate file type
 if (!file.type.startsWith('image/')) {
 setModalErrorMsg('Please upload a valid image file.');
 setFileInputKey(prev => prev + 1);
 return;
 }

 try {
 const dataUrl = await readFileAsDataUrl(file);
 setFormData({ ...formData, profilePhoto: dataUrl, avatarId: 'letter' });
 setModalErrorMsg(null);
 setFileInputKey(prev => prev + 1);
 } catch (err) {
 setModalErrorMsg('Failed to process image. Please try another file.');
 setFileInputKey(prev => prev + 1);
 }
 }}
 />
 Upload Photo
 </label>
 </div>

 {/* Form */}
 <div className="profile-modal-body">
 {/* Emoji Grid - now scrollable with form */}
 <div className="profile-avatar-emoji-grid">
 {AVATAR_OPTIONS.map((option) => {
 const isSelected = formData.avatarId === option.id && !formData.profilePhoto;
 return (
 <button
 key={option.id}
 type="button"
 className={`profile-avatar-emoji-tile ${isSelected ? 'is-selected' : ''}`}
 onClick={() => setFormData({
 ...formData,
 avatarId: option.id,
 profilePhoto: '',
 })}
 aria-label={option.label}
 >
 {option.emoji ? (
 <span className="profile-avatar-emoji-tile-glyph">{option.emoji}</span>
 ) : (
 <span className="profile-avatar-emoji-tile-letter">{initial}</span>
 )}
 </button>
 );
 })}
 </div>
 <label className="profile-field-label">First Name</label>
 <input
 type="text"
 className="profile-input"
 value={formData.firstName}
 onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
 placeholder="Enter your first name"
 />

 <label className="profile-field-label">Last Name</label>
 <input
 type="text"
 className="profile-input"
 value={formData.lastName}
 onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
 placeholder="Enter your last name"
 />

 <label className="profile-field-label">Date of Birth</label>
 <div className="profile-date-row">
 <select
 className={`profile-input profile-date-select ${birthDateError ? 'profile-input--error' : ''}`}
 value={formData.birthDay}
 onChange={(e) => {
 const day = e.target.value;
 const month = formData.birthMonth || '01';
 const year = formData.birthYear || '2000';
 setFormData({ ...formData, birthDay: day, birthMonth: month, birthYear: year, birthDate: `${year}-${month}-${day}` });
 if (birthDateError) setBirthDateError(false);
 }}
 >
 <option value="">Day</option>
 {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
 <option key={day} value={String(day).padStart(2, '0')}>{day}</option>
 ))}
 </select>
 <select
 className={`profile-input profile-date-select ${birthDateError ? 'profile-input--error' : ''}`}
 value={formData.birthMonth}
 onChange={(e) => {
 const month = e.target.value;
 const day = formData.birthDay || '01';
 const year = formData.birthYear || '2000';
 setFormData({ ...formData, birthDay: day, birthMonth: month, birthYear: year, birthDate: `${year}-${month}-${day}` });
 if (birthDateError) setBirthDateError(false);
 }}
 >
 <option value="">Month</option>
 {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, i) => (
 <option key={month} value={String(i + 1).padStart(2, '0')}>{month}</option>
 ))}
 </select>
 <select
 className={`profile-input profile-date-select ${birthDateError ? 'profile-input--error' : ''}`}
 value={formData.birthYear}
 onChange={(e) => {
 const year = e.target.value;
 const day = formData.birthDay || '01';
 const month = formData.birthMonth || '01';
 setFormData({ ...formData, birthDay: day, birthMonth: month, birthYear: year, birthDate: `${year}-${month}-${day}` });
 if (birthDateError) setBirthDateError(false);
 }}
 >
 <option value="">Year</option>
 {(() => {
 const currentYear = new Date().getFullYear();
 const maxYear = currentYear - 13;
 const minYear = 1920; // Allow scrolling back to 1920
 return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i).map(year => (
 <option key={year} value={String(year)}>{year}</option>
 ));
 })()}
 </select>
 </div>

 <label className="profile-field-label">Gender</label>
 <select
 className="profile-input"
 value={formData.gender}
 onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
 >
 {GENDER_OPTIONS.map((g) => (
 <option key={g} value={g}>{g || 'Select your gender'}</option>
 ))}
 </select>

 <label className="profile-field-label">Height (cm)</label>
 <div className="profile-number-input-wrapper">
 <input
 type="number"
 inputMode="numeric"
 className="profile-input profile-number-input"
 value={formData.height}
 onChange={(e) => setFormData({ ...formData, height: e.target.value.replace(/[^0-9]/g, '') })}
 placeholder="e.g. 175"
 />
 <div className="profile-number-spinners">
 <button
 type="button"
 className="profile-number-spinner-btn"
 onClick={() => setFormData({ ...formData, height: String(Math.min(240, parseInt(formData.height || 0) + 1)) })}
 aria-label="Increase height"
 >
 <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
 <path d="M7 4L4 8H10L7 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
 </svg>
 </button>
 <button
 type="button"
 className="profile-number-spinner-btn"
 onClick={() => setFormData({ ...formData, height: String(Math.max(90, parseInt(formData.height || 0) - 1)) })}
 aria-label="Decrease height"
 >
 <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
 <path d="M7 10L4 6H10L7 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
 </svg>
 </button>
 </div>
 </div>

 <label className="profile-field-label">Weight (kg)</label>
 <div className="profile-number-input-wrapper">
 <input
 type="number"
 inputMode="numeric"
 className="profile-input profile-number-input"
 value={formData.weight}
 onChange={(e) => setFormData({ ...formData, weight: e.target.value.replace(/[^0-9]/g, '') })}
 placeholder="e.g. 70"
 />
 <div className="profile-number-spinners">
 <button
 type="button"
 className="profile-number-spinner-btn"
 onClick={() => setFormData({ ...formData, weight: String(Math.min(300, parseInt(formData.weight || 0) + 1)) })}
 aria-label="Increase weight"
 >
 <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
 <path d="M7 4L4 8H10L7 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
 </svg>
 </button>
 <button
 type="button"
 className="profile-number-spinner-btn"
 onClick={() => setFormData({ ...formData, weight: String(Math.max(15, parseInt(formData.weight || 0) - 1)) })}
 aria-label="Decrease weight"
 >
 <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
 <path d="M7 10L4 6H10L7 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
 </svg>
 </button>
 </div>
 </div>

 <label className="profile-field-label">Sleep Goal</label>
 <select
 className="profile-input"
 value={formData.sleepGoal ?? ''}
 onChange={(e) => setFormData({ ...formData, sleepGoal: parseSleepGoal(e.target.value) })}
 >
 <option value="">Select your sleep goal</option>
 {SLEEP_GOAL_OPTIONS.map((option) => (
 <option key={option.value} value={option.value}>{option.label}</option>
 ))}
 </select>

 <div className="profile-field-label-with-tooltip">
 <label className="profile-field-label">Typical Day</label>
 <FieldTooltip content="Describes your daily activity level. Examples:<br/>• Mostly Sitting (office worker, desk job)<br/>• Mostly Standing (retail, teacher)<br/>• Mostly Moving (delivery driver, construction worker)" />
 </div>
 <select
 className="profile-input"
 value={formData.typicalDay}
 onChange={(e) => setFormData({ ...formData, typicalDay: e.target.value })}
 >
 {TYPICAL_DAY_OPTIONS.map((d) => (
 <option key={d} value={d}>{d || 'Select your typical day'}</option>
 ))}
 </select>

 <div className="profile-field-label-with-tooltip">
 <label className="profile-field-label">Training Background</label>
 <FieldTooltip content="Ranges from:<br/>• Beginner (no regular exercise)<br/>• Intermediate (some regular exercise)<br/>• Long-term consistent training (e.g., athletes, regular gym-goers for years)" />
 </div>
 <select
 className="profile-input"
 value={formData.trainingBackground === '' || formData.trainingBackground == null ? '' : String(formData.trainingBackground)}
 onChange={(e) => {
 const v = e.target.value;
 setFormData({
 ...formData,
 // Keep the empty-string path so the user can clear the field;
 // otherwise coerce back to a number because the backend expects
 // Integer, not string.
 trainingBackground: v === '' ? '' : Number(v),
 });
 }}
 >
 <option value="">Select your training background</option>
 {TRAINING_BACKGROUND_OPTIONS.map((option) => (
 <option key={String(option.value)} value={String(option.value)}>
 {option.label}
 </option>
 ))}
 </select>

 <label className="profile-field-label">Clinical Story</label>
 <textarea
 className="profile-input profile-textarea"
 value={formData.clinicalStory}
 onChange={(e) => setFormData({ ...formData, clinicalStory: e.target.value })}
 placeholder="Any relevant medical history..."
 rows={4}
 />

 <label className="profile-field-label">Notes</label>
 <textarea
 className="profile-input profile-textarea"
 value={formData.notes}
 onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
 placeholder="Add notes here..."
 rows={4}
 />
 </div>
 </div>
 </div>,
 document.body
 );
}

// ─── MAIN ───

function Profile() {
 const navigate = useNavigate();

 const [isModalOpen, setModalOpen] = useState(false);
 const [isLoading, setIsLoading] = useState(true);
 const [isSaving, setIsSaving] = useState(false);
 const [errorMsg, setErrorMsg] = useState(null);
 const [successMsg, setSuccessMsg] = useState(null);
 const [birthDateError, setBirthDateError] = useState(false);

 const [formData, setFormData] = useState({
 firstName: '',
 lastName: '',
 birthDate: '',
 birthDay: '',
 birthMonth: '',
 birthYear: '',
 gender: '',
 clinicalStory: '',
 notes: '',
 height: '',
 weight: '',
 sleepGoal: null,
 typicalDay: '',
 // Empty string rather than null so <select> stays controlled.
 trainingBackground: '',
 profilePhoto: '',
 avatarId: 'letter',
 });

 // Load profile on mount
 useEffect(() => {
 let cancelled = false;
 (async () => {
 setIsLoading(true);
 const auth = getAuth();
 if (!auth) {
 if (!cancelled) {
 setFormData({
 firstName: 'User',
 lastName: '',
 birthDay: '',
 birthMonth: '',
 birthYear: '',
 gender: '',
 clinicalStory: '',
 notes: '',
 height: '',
 weight: '',
 sleepGoal: null,
 typicalDay: '',
 trainingBackground: '',
 profilePhoto: '',
 avatarId: 'letter',
 });
 setIsLoading(false);
 }
 return;
 }
 const profile = await fetchUserProfile(auth.token, auth.userId);
 const localExtras = loadProfileExtras(auth.userId);
 if (cancelled) return;
 if (profile) {
 // Backend stores firstName/lastName separately — prefer those.
 // Fall back to email prefix if both are empty.
 const firstName = (profile.firstName || '').trim();
 const lastName = (profile.lastName || '').trim();
 const hasAnyName = firstName || lastName;
 const storedDateOfBirth = profile.birthDate || localExtras.dateOfBirth || '';
 const parsedDateOfBirth = splitDateOfBirth(storedDateOfBirth);
 setFormData({
 firstName: hasAnyName ? firstName : (profile.email?.split('@')[0] || 'User'),
 lastName: hasAnyName ? lastName : '',
 birthDate: storedDateOfBirth,
 birthDay: profile.birthDate ? parsedDateOfBirth.birthDay : (localExtras.birthDay || parsedDateOfBirth.birthDay || ''),
 birthMonth: profile.birthDate ? parsedDateOfBirth.birthMonth : (localExtras.birthMonth || parsedDateOfBirth.birthMonth || ''),
 birthYear: profile.birthDate ? parsedDateOfBirth.birthYear : (localExtras.birthYear || parsedDateOfBirth.birthYear || ''),
 gender: profile.gender || '',
 clinicalStory: profile.clinicalStory || '',
 notes: Array.isArray(profile.notes)
 ? profile.notes.join('\n')
 : (profile.notes || ''),
 height: profile.height ? String(profile.height) : '',
 weight: profile.weight ? String(profile.weight) : '',
 sleepGoal: parseSleepGoal(profile.sleepGoal),
 // typicalDay + trainingBackground replace the old activityLevel.
 typicalDay: profile.typicalDay || '',
 trainingBackground:
 typeof profile.trainingBackground === 'number' ? profile.trainingBackground : '',
 profilePhoto: profile.profilePhoto || localExtras.profilePhoto || '',
 avatarId: profile.avatarId || localExtras.avatarId || 'letter',
 });
 } else {
 setFormData({
 firstName: 'User',
 lastName: '',
 birthDate: localExtras.dateOfBirth || '',
 birthDay: localExtras.birthDay || '',
 birthMonth: localExtras.birthMonth || '',
 birthYear: localExtras.birthYear || '',
 gender: '',
 clinicalStory: '',
 notes: '',
 height: '',
 weight: '',
 sleepGoal: null,
 typicalDay: '',
 trainingBackground: '',
 profilePhoto: localExtras.profilePhoto || '',
 avatarId: localExtras.avatarId || 'letter',
 });
 }
 setIsLoading(false);
 })();
 return () => { cancelled = true; };
 }, []);

 // Auto-clear success/error messages
 useEffect(() => {
 if (!successMsg && !errorMsg) return;
 const t = setTimeout(() => { setSuccessMsg(null); setErrorMsg(null); }, 3500);
 return () => clearTimeout(t);
 }, [successMsg, errorMsg]);

 const handleSave = async () => {
 const auth = getAuth();
 if (!auth) {
 setErrorMsg('Please log in to save your profile.');
 return;
 }

 setIsSaving(true);
 const birthDate = `${formData.birthYear}-${formData.birthMonth}-${formData.birthDay}`;
 const derivedAge = calculateAgeFromDateOfBirth(birthDate);
 if (!formData.birthDay || !formData.birthMonth || !formData.birthYear || derivedAge == null) {
 setIsSaving(false);
 setBirthDateError(true);
 setErrorMsg('Please provide your complete date of birth before saving.');
 return;
 }

 const payload = {
 firstName: formData.firstName?.trim() || undefined,
 lastName: formData.lastName?.trim() || undefined,
 age: derivedAge || undefined,
 birthDate: birthDate || undefined,
 gender: formData.gender || undefined,
 clinicalStory: formData.clinicalStory || undefined,
 notes: formData.notes
 ? formData.notes.split('\n').filter(n => n.trim())
 : undefined,
 height: parseInt(formData.height, 10) || undefined,
 weight: parseInt(formData.weight, 10) || undefined,
 sleepGoal: formData.sleepGoal ?? undefined,
 // Replaces the old activityLevel field — backend now splits it.
 typicalDay: formData.typicalDay || undefined,
 trainingBackground:
 typeof formData.trainingBackground === 'number' ? formData.trainingBackground : undefined,
 profilePhoto: formData.profilePhoto || '',
 avatarId: formData.avatarId || 'letter',
 };

 const updatedProfile = await updateUserProfile(auth.token, auth.userId, payload);
 setIsSaving(false);

 if (updatedProfile) {
 saveProfileExtras(auth.userId, {
 dateOfBirth: birthDate,
 birthDay: formData.birthDay,
 birthMonth: formData.birthMonth,
 birthYear: formData.birthYear,
 profilePhoto: updatedProfile.profilePhoto || formData.profilePhoto,
 avatarId: updatedProfile.avatarId || formData.avatarId,
 });
 setFormData((prev) => ({
 ...prev,
 birthDate,
 profilePhoto: updatedProfile.profilePhoto || prev.profilePhoto,
 avatarId: updatedProfile.avatarId || prev.avatarId,
 }));
 setSuccessMsg('Your profile has been updated.');
 setModalOpen(false);
 } else {
 setErrorMsg('Failed to save profile. Please try again.');
 }
 };

 const handleLogout = async () => {
 const auth = getAuth();
 if (auth) {
 await logoutBackend(auth.token);
 }
 clearAuth();
 // Also clear any derived per-user caches (no flags to wipe on web yet,
 // but this is the place to add them if you introduce any).
 navigate('/');
 };

 const displayName = [formData.firstName, formData.lastName]
 .filter(p => p && p.trim())
 .join(' ')
 .trim() || 'User';

 const initial = displayName.charAt(0).toUpperCase();
 const birthDate = `${formData.birthYear}-${formData.birthMonth}-${formData.birthDay}`;
 const derivedAge = calculateAgeFromDateOfBirth(birthDate);
 const selectedAvatar = AVATAR_OPTIONS.find((option) => option.id === formData.avatarId);
 const infoTags = [
 derivedAge ? `${derivedAge} years` : null,
 formData.gender || null,
 getTrainingBackgroundLabel(formData.trainingBackground) !== '—'
 ? getTrainingBackgroundLabel(formData.trainingBackground)
 : null,
 ].filter(Boolean);

 if (isLoading) {
 return (
 <div className="profile-page-wrapper">
 <Navbar />
 <div className="profile-loading-wrap">
 <SpinnerIcon size={36} />
 <p>Loading profile...</p>
 </div>
 <Footer />
 </div>
 );
 }

 return (
 <div className="profile-page-wrapper">
 <Navbar />

 <div className="profile-container">
 {/* Toast messages */}
 {successMsg && (
 <div className="profile-toast profile-toast--success">
 {successMsg}
 <button onClick={() => setSuccessMsg(null)} className="profile-toast-close" type="button">
 <CloseIcon size={14} color="#226149" />
 </button>
 </div>
 )}
 {errorMsg && !isModalOpen && (
 <div className="profile-toast profile-toast--error">
 {errorMsg}
 <button onClick={() => setErrorMsg(null)} className="profile-toast-close" type="button">
 <CloseIcon size={14} color="#B93A3A" />
 </button>
 </div>
 )}

 {/* ── Profile Header Card ── */}
 <div className="profile-card profile-header-card">
 <div className="profile-header-top">
 {/* Avatar */}
 <div className="profile-avatar-outer">
 <div className="profile-avatar">
 {formData.profilePhoto ? (
 <img src={formData.profilePhoto} alt="Profile" className="profile-avatar-image" />
 ) : selectedAvatar?.emoji ? (
 <span className="profile-avatar-emoji">{selectedAvatar.emoji}</span>
 ) : (
 <span className="profile-avatar-letter">{initial || 'U'}</span>
 )}
 </div>
 <span className="profile-online-dot" />
 </div>

 {/* Name + tags */}
 <div className="profile-info">
 <h1 className="profile-name">{displayName}</h1>
 {infoTags.length > 0 && (
 <div className="profile-tags-row">
 {infoTags.map((tag, i) => (
 <span key={i} className="profile-tag">{tag}</span>
 ))}
 </div>
 )}
 </div>

 {/* Edit button (right side on desktop) */}
 <button
 type="button"
 className="profile-edit-btn"
 onClick={() => setModalOpen(true)}
 >
 <EditIcon size={16} color="#5CB89A" />
 <span>Edit Profile</span>
 </button>
 </div>
 </div>

 {/* ── Health Details Card ── */}
 <div className="profile-card profile-details-card">
 <h2 className="profile-section-title">Health Details</h2>

 <div className="profile-detail-row">
 <div className="profile-detail-icon-wrap">
 <UserInfoIcon size={16} color="#5CB89A" />
 </div>
 <div className="profile-detail-content">
 <span className="profile-detail-label">Clinical Story</span>
 <span className="profile-detail-value">
 {formData.clinicalStory || '—'}
 </span>
 </div>
 </div>

 <div className="profile-detail-divider" />

 <div className="profile-detail-row">
 <div className="profile-detail-icon-wrap">
 <NoteIcon size={16} color="#5CB89A" />
 </div>
 <div className="profile-detail-content">
 <span className="profile-detail-label">Notes</span>
 <span className="profile-detail-value">
 {formData.notes
 ? formData.notes.split('\n').filter(n => n.trim()).join(' · ')
 : '—'}
 </span>
 </div>
 </div>
 </div>

 {/* ── Menu Card ── */}
 <div className="profile-card profile-menu-card">
 <button
 type="button"
 className="profile-menu-item"
 onClick={() => navigate('/settings')}
 >
 <div className="profile-menu-icon-wrap">
 <SettingsIcon size={22} color="#434F4D" />
 </div>
 <span className="profile-menu-label">Settings</span>
 <ChevronRightIcon size={18} color="#A0ABA8" />
 </button>

 <button
 type="button"
 className="profile-menu-item"
 onClick={() => navigate('/faq')}
 >
 <div className="profile-menu-icon-wrap">
 <HelpIcon size={22} color="#434F4D" />
 </div>
 <span className="profile-menu-label">Help &amp; FAQ</span>
 <ChevronRightIcon size={18} color="#A0ABA8" />
 </button>

 <button
 type="button"
 className="profile-menu-item profile-menu-item--last"
 onClick={handleLogout}
 >
 <div className="profile-menu-icon-wrap profile-menu-icon-wrap--danger">
 <LogOutIcon size={22} color="#DC4545" />
 </div>
 <span className="profile-menu-label profile-menu-label--danger">Log Out</span>
 <ChevronRightIcon size={18} color="#A0ABA8" />
 </button>
 </div>
 </div>

 <Footer />

 <EditProfileModal
 open={isModalOpen}
 onClose={() => setModalOpen(false)}
 onSave={handleSave}
 formData={formData}
 setFormData={setFormData}
 isSaving={isSaving}
 birthDateError={birthDateError}
 setBirthDateError={setBirthDateError}
 />
 </div>
 );
}

export default Profile;